import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faArrowDown
} from "@fortawesome/free-solid-svg-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import styles from "../../styles/patient-page-modules/Chatbot.module.css";
import Button from "../../components/button-v1";
import toast from "react-hot-toast";
import axios from "axios";
import StreamingText from "../streamingText";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaSheetPlastic } from "react-icons/fa6";
import { deleteCarePlan, getCarePlan } from "../../services/patient/carePlan";
import { getPatientGeneralDetails, checkPatientPhone } from "../../services/patient/details";
import CarePlanModal from "./patient-modals/carePlanModal";
import CollapsableSection from "../../components/shared-modules/collapsable";
import AlertDialog from "../../components/alert-dialogue";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../components/ui/dialog";
import UploadFiles from "../../components/shared-modules/upload-files/index";
import { uploadPatientDocuments } from "../../services/patient/documents";
import { useMutation } from "@tanstack/react-query";
import { BASE_URL_ML } from "../../utils/constants";
import { useSearchParams } from "react-router-dom";
import { useToggle } from "../../hooks/useToggle";
import ResetCareplan from "../shared-modules/resetCareplanModal/ResetCareplan";
import { GrUpload } from "react-icons/gr";
import CcmReferenceDrawer from "./CcmReferenceDrawer";

// Titles shown in the reference drawer header.
const CCM_REFERENCE_LABELS = {
  "condition-guidelines": { label: "Condition Guidelines" },
  "warning-signs": { label: "Red-Flag Warning Signs" }
};

// Quick-prompt chips are disabled (commented out) per product request; kept
// here so they can be re-enabled if the feature returns.
// const QUICK_PROMPTS = [
//   "Create CCM plan",
//   "Analyze RPM",
//   "Suggest foods",
//   "Summarize patient's detail"
// ];

// "Restart" icon (iconamoon-restart) from Figma; pairs with "Restart chat" text in the toolbar pill.
const RestartIcon = ({ width = 16, height = 16, color = "#ffffff" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M7.99999 2C9.41536 2.00002 10.7852 2.5004 11.8673 3.4127C12.9494 4.325 13.6742 5.59047 13.9135 6.98546C14.1528 8.38045 13.8913 9.81513 13.1751 11.0359C12.459 12.2568 11.3343 13.1851 9.99984 13.6569C8.66542 14.1287 7.20717 14.1135 5.88283 13.6141C4.55849 13.1147 3.45334 12.1632 2.76269 10.9278C2.07205 9.69233 1.84038 8.25252 2.10864 6.86281C2.3769 5.47309 3.12781 4.22295 4.22865 3.33333"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2 3H4.66667V5.66667"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// "Send" icon (iconamoon-send-light) from Figma; sits inside the navy pill input send button.
const SendIcon = ({ width = 22, height = 22, color = "#ffffff" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4.99994 12L4.39594 6.563C4.22294 5.007 5.82494 3.864 7.23994 4.535L19.1839 10.193C20.7089 10.915 20.7089 13.085 19.1839 13.807L7.23994 19.466C5.82494 20.136 4.22294 18.994 4.39594 17.438L4.99994 12ZM4.99994 12H11.9999"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// "Generate Notes" icon from Figma (provided as SVG by product).
// Designed for the navy/secondary-blue circular toolbar button on TOCA AI panel.
const GenerateNotesIcon = ({ width = 18, height = 16, color = "#ffffff" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 24 22" fill="none" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.375 0H6.375C4.305 0 2.625 1.53897 2.625 3.4375V18.5625C2.625 20.461 4.305 22 6.375 22H15.375C16.3687 22 17.325 21.6377 18.0262 20.9932C18.7312 20.3486 19.125 19.4741 19.125 18.5625C19.125 14.9163 19.125 8.9375 19.125 8.9375C19.125 8.558 18.7875 8.25 18.375 8.25C17.9625 8.25 17.625 8.558 17.625 8.9375V18.5625C17.625 19.1094 17.3887 19.634 16.965 20.021C16.545 20.4078 15.9712 20.625 15.375 20.625C12.7613 20.625 8.98875 20.625 6.375 20.625C5.13375 20.625 4.125 19.7017 4.125 18.5625V3.4375C4.125 2.29831 5.13375 1.375 6.375 1.375H15.375C15.7875 1.375 16.125 1.067 16.125 0.6875C16.125 0.308 15.7875 0 15.375 0ZM6.375 17.1875H15.375C15.7875 17.1875 16.125 16.8795 16.125 16.5C16.125 16.1205 15.7875 15.8125 15.375 15.8125H6.375C5.9625 15.8125 5.625 16.1205 5.625 16.5C5.625 16.8795 5.9625 17.1875 6.375 17.1875ZM6.375 14.4375H15.375C15.7875 14.4375 16.125 14.1295 16.125 13.75C16.125 13.3705 15.7875 13.0625 15.375 13.0625H6.375C5.9625 13.0625 5.625 13.3705 5.625 13.75C5.625 14.1295 5.9625 14.4375 6.375 14.4375ZM6.375 11.6875H12C12.4125 11.6875 12.75 11.3795 12.75 11C12.75 10.6205 12.4125 10.3125 12 10.3125H6.375C5.9625 10.3125 5.625 10.6205 5.625 11C5.625 11.3795 5.9625 11.6875 6.375 11.6875ZM14.9625 2.88131C14.8613 2.59806 14.5763 2.40625 14.25 2.40625C13.9237 2.40625 13.6387 2.59806 13.5375 2.88131L12.6412 5.40168L9.8925 6.22118C9.585 6.31331 9.375 6.57731 9.375 6.875C9.375 7.17269 9.585 7.43669 9.8925 7.52882L12.6412 8.34832L13.5375 10.8687C13.6387 11.1519 13.9237 11.3438 14.25 11.3438C14.5763 11.3438 14.8613 11.1519 14.9625 10.8687L15.8588 8.34832L18.6075 7.52882C18.915 7.43669 19.125 7.17269 19.125 6.875C19.125 6.57731 18.915 6.31331 18.6075 6.22118L15.8588 5.40168L14.9625 2.88131ZM6.375 8.9375H8.625C9.0375 8.9375 9.375 8.6295 9.375 8.25C9.375 7.8705 9.0375 7.5625 8.625 7.5625H6.375C5.9625 7.5625 5.625 7.8705 5.625 8.25C5.625 8.6295 5.9625 8.9375 6.375 8.9375ZM14.25 5.31712L14.55 6.15966C14.625 6.369 14.8012 6.53297 15.03 6.60103L15.9488 6.875L15.03 7.14897C14.8012 7.21703 14.625 7.381 14.55 7.59034L14.25 8.43288L13.95 7.59034C13.8787 7.381 13.6988 7.21703 13.47 7.14897L12.5512 6.875L13.47 6.60103C13.6988 6.53297 13.8787 6.369 13.95 6.15966L14.25 5.31712ZM6.375 6.1875H8.625C9.0375 6.1875 9.375 5.8795 9.375 5.5C9.375 5.1205 9.0375 4.8125 8.625 4.8125H6.375C5.9625 4.8125 5.625 5.1205 5.625 5.5C5.625 5.8795 5.9625 6.1875 6.375 6.1875ZM19.4625 0.475059C19.3613 0.191809 19.0763 0 18.75 0C18.4237 0 18.1387 0.191809 18.0375 0.475059L17.6962 1.43894L16.6425 1.75243C16.335 1.84456 16.125 2.10856 16.125 2.40625C16.125 2.70394 16.335 2.96794 16.6425 3.06007L17.6962 3.37356L18.0375 4.33744C18.1387 4.62069 18.4237 4.8125 18.75 4.8125C19.0763 4.8125 19.3613 4.62069 19.4625 4.33744L19.8038 3.37356L20.8575 3.06007C21.165 2.96794 21.375 2.70394 21.375 2.40625C21.375 2.10856 21.165 1.84456 20.8575 1.75243L19.8038 1.43894L19.4625 0.475059Z"
      fill={color}
    />
  </svg>
);

// "Open in full window" icon from Figma (provided as SVG by product).
// Click behavior intentionally deferred per current scope.
const ExpandIcon = ({ width = 16, height = 17, color = "#0D1139" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 20 21" fill="none" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.87517 2.75161C9.87583 3.09955 9.5965 3.38211 9.25133 3.38274C7.72031 3.38552 6.61303 3.40704 5.76531 3.54536C4.93587 3.68068 4.42431 3.91789 4.04033 4.30495C3.62662 4.72197 3.38471 5.28915 3.25673 6.24866C3.12633 7.2263 3.125 8.51279 3.125 10.3125C3.125 12.1123 3.12633 13.3987 3.25673 14.3764C3.3847 15.3359 3.62662 15.9031 4.04033 16.3201C4.45403 16.7371 5.01671 16.981 5.96861 17.1099C6.93849 17.2414 8.21473 17.2427 10.0003 17.2427C11.7857 17.2427 13.0619 17.2414 14.0318 17.1099C14.9838 16.981 15.5464 16.7371 15.9601 16.3201C16.3441 15.933 16.5794 15.4174 16.7137 14.5813C16.8509 13.7268 16.8723 12.6106 16.875 11.0674C16.8756 10.7195 17.1559 10.4379 17.5012 10.4386C17.8463 10.4392 18.1256 10.7217 18.125 11.0697C18.1223 12.5922 18.1036 13.8115 17.9476 14.7826C17.7888 15.7722 17.4778 16.5722 16.844 17.2111C16.1593 17.9012 15.2889 18.2109 14.1984 18.3588C13.1356 18.5027 11.7753 18.5027 10.0477 18.5027H9.95275C8.22518 18.5027 6.86487 18.5027 5.80205 18.3588C4.71153 18.2109 3.84111 17.9012 3.15644 17.2111C2.47177 16.5209 2.16448 15.6435 2.01787 14.5442C1.87498 13.473 1.87499 12.1018 1.875 10.3604V10.2647C1.87499 8.52329 1.87498 7.15209 2.01788 6.08077C2.16449 4.98153 2.47177 4.10414 3.15644 3.414C3.79026 2.77511 4.58392 2.46171 5.56558 2.30155C6.52898 2.14436 7.73869 2.12548 9.24908 2.12274C9.59425 2.12211 9.87458 2.40367 9.87517 2.75161Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.7083 2.75279C12.7083 2.40486 12.9881 2.12278 13.3333 2.12274L13.3407 2.12275L13.3609 2.12279L13.4373 2.12312C13.5032 2.12349 13.5982 2.12425 13.7154 2.12574C13.9497 2.12875 14.2735 2.13477 14.6317 2.14686C15.3346 2.17059 16.2153 2.21886 16.7994 2.32312C17.1069 2.37802 17.374 2.52647 17.5733 2.74059C17.5749 2.74238 17.5766 2.74417 17.5783 2.74598C17.7551 2.93817 17.8777 3.18255 17.9263 3.45926C18.0296 4.0479 18.0774 4.93554 18.101 5.64405C18.113 6.00507 18.119 6.33148 18.122 6.56758C18.1235 6.68573 18.1243 6.7815 18.1246 6.84796L18.1249 6.92487L18.125 6.94526V6.95212C18.125 7.30006 17.8452 7.58274 17.5 7.58274C17.1548 7.58274 16.875 7.30069 16.875 6.95277V6.94693L16.8749 6.9283L16.8747 6.85517C16.8743 6.79133 16.8736 6.69855 16.8721 6.5837C16.8692 6.3538 16.8633 6.03641 16.8517 5.68627C16.8375 5.26022 16.8152 4.80198 16.7812 4.39769L12.9403 8.23992C12.6953 8.48499 12.2995 8.48347 12.0564 8.23651C11.8133 7.98954 11.8148 7.59065 12.0598 7.34557L15.9221 3.48192C15.5082 3.44499 15.0316 3.42106 14.5898 3.40615C14.2425 3.39443 13.9276 3.38857 13.6995 3.38564C13.5856 3.38418 13.4935 3.38346 13.4302 3.38309L13.3577 3.38278L13.3392 3.38275L13.3336 3.38274C12.9884 3.38276 12.7083 3.10073 12.7083 2.75279Z"
      fill={color}
    />
  </svg>
);

// Welcome panel renders the new "Start chatting with TOCA AI" view.
// Same panel chrome as the active chat (light bg, blue glow, border),
// but with: a simpler toolbar (just the Expand placeholder icon on the right),
// a 4-step guidance list in the body, and the same input bar + quick prompts.
// Submitting from the input or clicking a quick prompt routes the user into
// the existing document-select flow.
const ChatbotWelcomePanel = ({
  title,
  subtitle,
  steps,
  onStartWithoutFiles,
  onStartWithFiles,
  isUploadingDocuments,
  uploadedDocuments,
  onRemoveUploadedDocument,
  setShowUploadModal,
  styles
}) => {
  return (
    <div className={styles.chatbot_redesign_panel}>
      <div className={styles.welcome_toolbar}>
        <div className={styles.welcome_title}>{title}</div>
      </div>

      <div className={styles.welcome_body}>
        <div className={styles.welcome_subtitle_centered}>{subtitle}</div>
        <div className={styles.welcome_steps}>
          {steps.map((step, i) => (
            <div key={i} className={styles.welcome_step}>
              <span className={styles.welcome_step_badge}>{i + 1}</span>
              <div className={styles.welcome_step_content}>
                <div className={styles.welcome_step_title}>{step.title}</div>
                <div className={styles.welcome_step_desc}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {uploadedDocuments.length === 0 && (
        <div className={styles.rpm_welcome_actions}>
          <Button variant="ghost" className={styles.start_button} onClick={onStartWithoutFiles}>
            Start Chat
          </Button>
          <Button
            variant="ghost"
            className={styles.upload_document_btn}
            onClick={() => setShowUploadModal(true)}
            // loading={isUploadingDocuments}
            disabled={isUploadingDocuments}
          >
            Upload Document
          </Button>
        </div>
      )}

      {uploadedDocuments.length > 0 && (
        <div className={styles.welcome_upload_box}>
          <div className={styles.input_documents_row}>
            {uploadedDocuments.map((doc) => (
              <div key={doc.id} className={styles.input_document_chip}>
                <div className={styles.input_document_icon} aria-hidden="true">
                  <img src="/assets/images/pdficon.svg" alt="PDF" />
                </div>
                <div className={styles.input_document_text}>
                  <div className={styles.input_document_name}>{doc.name}</div>
                  <div className={styles.input_document_type}>PDF</div>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.welcome_upload_actions}>
            <button
              type="button"
              className={styles.start_button}
              disabled={isUploadingDocuments}
              onClick={onStartWithFiles}
              title="Start Chat"
              aria-label="Start Chat"
            >
              Start Chat
            </button>
          </div>
        </div>
      )}

      {/* Quick-prompt chips commented out per product request.
      <div className={styles.quick_prompts}>
        {QUICK_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            className={styles.quick_prompt_btn}
            onClick={() => {
              setMessageValue(prompt);
              setTimeout(() => onStart(), 0);
            }}
          >
            {prompt}
          </button>
        ))}
      </div>
      */}
    </div>
  );
};

const RPM_STEPS = [
  { title: "Patient Context Loaded", desc: "TOCA AI is aware of the patient's available documents and reports" },
  { title: "Review Pre-Call Summary", desc: "CoPilot highlights key vitals, alerts, and talking points before your call" },
  { title: "Guided Conversation", desc: "Receive suggested questions and clinical prompts during the patient call" },
  { title: "Post-Call Documentation", desc: "Capture notes and outcomes; CoPilot helps structure your documentation" }
];

// Redesigned chatbot panel used for both RPM and CCM active-chat views.
// Renders: toolbar (Restart, placeholder, Generate Notes) + chat scroll +
// Recommended Question placeholder + Ask Anything input bar + quick prompts.
const ChatbotRedesignedPanel = ({
  tab,
  messages,
  messageValue,
  setMessageValue,
  chatContainerRef,
  checkScrollPosition,
  scrollToBottom,
  showScrollButton,
  handleSubmit,
  isGeneratingNotes,
  isToolbarGenOpen,
  setIsToolbarGenOpen,
  toolbarGenBtnRef,
  toolbarDropdownPos,
  updateToolbarDropdownPos,
  patientDetails,
  phoneCheckData,
  setPhoneAlertMessage,
  setPhoneAlertOpen,
  setIsConfirmGenerateNotesOpen,
  handleGenerateChatbotNotes,
  setIsRestartConfirmOpen,
  genCareplanAction,
  ccmQuickActions,
  referenceDrawer,
  suppressPendingBubble,
  isUploadingDocuments,
  uploadedDocuments,
  onRemoveUploadedDocument,
  setShowUploadModal,
  styles
}) => {
  const lastBotIndex = [...messages].reverse().findIndex((m) => m.type === "bot");
  const actualLastBotIndex = lastBotIndex === -1 ? -1 : messages.length - 1 - lastBotIndex;
  const isBotBusy = messages.length > 0 && messages[messages.length - 1].loading;
  const visibleMessages = messages
    .map((msg, index) => ({ msg, index }))
    .filter(
      ({ msg }) => !msg.quickAction && !(suppressPendingBubble && msg.loading)
    );
  // Recommended Question section is disabled until the backend provides
  // suggested_questions[]; its only consumer (the commented-out block below)
  // is off, so keep this gate commented to avoid an unused-var build error.
  // const hasBotResponse = messages.some(
  //   (m) => m.type === "bot" && !m.loading && (m.streamed || m.text)
  // );
  const submitDisabled =
    isBotBusy || (!(messageValue || "").trim() && uploadedDocuments.length === 0);

  return (
    <div className={styles.chatbot_redesign_panel} style={{ position: "relative" }}>
      {/* Toolbar */}
      <div className={styles.chatbot_toolbar}>
        <button
          type="button"
          className={styles.toolbar_restart}
          onClick={() => setIsRestartConfirmOpen(true)}
          disabled={isGeneratingNotes}
          aria-label="Restart chat"
        >
          <RestartIcon />
          <span>Restart chat</span>
        </button>
        <div className={styles.toolbar_actions}>
          <button
            type="button"
            className={styles.toolbar_placeholder_btn}
            title="Open in full window"
            aria-label="Open in full window"
            /* Behavior deferred per scope (not yet implemented) */
          >
            <ExpandIcon />
          </button>
          <div
            ref={toolbarGenBtnRef}
            style={{ position: "relative", display: "inline-block" }}
            onMouseEnter={() => {
              if (!isGeneratingNotes) {
                updateToolbarDropdownPos();
                setIsToolbarGenOpen(true);
              }
            }}
            onMouseLeave={() => setIsToolbarGenOpen(false)}
          >
            <button
              type="button"
              className={styles.toolbar_notes_btn}
              aria-label="Generate Notes"
              disabled={isGeneratingNotes}
            >
              {isGeneratingNotes ? (
                <FontAwesomeIcon icon={faCircle} className={styles.blinking_loader} />
              ) : (
                <GenerateNotesIcon />
              )}
            </button>
            {isToolbarGenOpen && !isGeneratingNotes && (
              <div
                className={styles.hoverDropdownMenu}
                style={{
                  top: `${toolbarDropdownPos.top}px`,
                  left: `${toolbarDropdownPos.left}px`,
                  transform: toolbarDropdownPos.isAbove
                    ? "translate(-50%, -100%)"
                    : "translate(-50%, 0)",
                  marginTop: toolbarDropdownPos.isAbove ? "-10px" : "10px"
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: "20px",
                    background: "transparent",
                    bottom: toolbarDropdownPos.isAbove ? "-20px" : "auto",
                    top: toolbarDropdownPos.isAbove ? "auto" : "-20px",
                    zIndex: -1
                  }}
                />
                <div
                  className={styles.hoverDropdownItem}
                  onClick={() => {
                    if (!patientDetails?.user?.primary_phone) {
                      setPhoneAlertMessage(
                        'This patient does not have a primary phone number on file. Please use the "Generate using chat only" option.'
                      );
                      setPhoneAlertOpen(true);
                      setIsToolbarGenOpen(false);
                      return;
                    }
                    if (phoneCheckData?.exists) {
                      setPhoneAlertMessage(
                        'This patient\'s phone number is associated with another patient. Please use the "Generate using chat only" option.'
                      );
                      setPhoneAlertOpen(true);
                      setIsToolbarGenOpen(false);
                      return;
                    }
                    setIsConfirmGenerateNotesOpen(true);
                    setIsToolbarGenOpen(false);
                  }}
                >
                  <div className={styles.hoverDropdownItemTitle}>Generate using call transcription</div>
                  <div className={styles.hoverDropdownItemDesc}>
                    Uses the RingCentral call recording along with chat history
                  </div>
                </div>
                <div
                  className={styles.hoverDropdownItem}
                  onClick={() => {
                    handleGenerateChatbotNotes(false);
                    setIsToolbarGenOpen(false);
                  }}
                >
                  <div className={styles.hoverDropdownItemTitle}>Generate using chat only</div>
                  <div className={styles.hoverDropdownItemDesc}>
                    Generates notes from chat conversation only, no call recording
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chat scroll area with new asymmetric bubbles */}
      <div ref={chatContainerRef} className={styles.chat_scroll} onScroll={checkScrollPosition}>
        {visibleMessages.map(({ msg, index }) => {
          const isUser = msg.type !== "bot";
          return (
            <div
              key={index}
              className={`${styles.bubble} ${
                isUser ? styles.bubble_user : styles.bubble_bot
              }`}
            >
              {msg.loading ? (
                <FontAwesomeIcon icon={faCircle} className={styles.blinking_loader} />
              ) : msg.streaming ? (
                <StreamingText
                  text={msg.text?.trim()}
                  isRealStream={true}
                  className={styles.bubble_markdown}
                />
              ) : msg.streamed ? (
                <ReactMarkdown
                  className={styles.bubble_markdown}
                  remarkPlugins={[remarkGfm]}
                >
                  {msg.text?.trim()}
                </ReactMarkdown>
              ) : msg.type === "bot" && index === actualLastBotIndex ? (
                <StreamingText
                  text={msg.text?.trim()}
                  className={styles.bubble_markdown}
                />
              ) : (
                msg.text?.trim()
              )}
            </div>
          );
        })}

        {/* Recommended Question section is disabled until the backend provides
            suggested_questions[] on the /chatbot/chat response. Kept here
            (commented, not deleted) so it can be re-enabled once wired.
        {hasBotResponse && (
          <div className={styles.recommended_section}>
            <div className={styles.recommended_label}>Recommended Question</div>
            <div className={styles.recommended_box}>Suggested follow-up coming soon.</div>
            <div className={styles.recommended_box}>Suggested follow-up coming soon.</div>
          </div>
        )} */}
      </div>

      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className={styles.scroll_to_bottom_btn}
          aria-label="Scroll to latest"
        >
          <FontAwesomeIcon icon={faArrowDown} />
        </button>
      )}

      {/* Reference drawer for CCM quick-action payloads. Docked between the
          transcript and the input pill so it stays readable while the chat*/}
      {referenceDrawer}

      {/* Ask Anything input bar — single pill with text + upload + send all inside.
          flex layout ensures the textarea's flex:1 grows only into available space,
          so long text never slides behind the upload/send icons. */}
      <div className={styles.input_pill}>
        {uploadedDocuments.length > 0 && (
          <div className={styles.input_documents_row}>
            {uploadedDocuments.map((doc) => (
              <div key={doc.id} className={styles.input_document_chip}>
                <div className={styles.input_document_icon} aria-hidden="true">
                <img src="/assets/images/pdficon.svg" alt="PDF" />
                </div>
                <div className={styles.input_document_text}>
                  <div className={styles.input_document_name}>{doc.name}</div>
                  <div className={styles.input_document_type}>PDF</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className={styles.input_main_row}>
          
          <textarea
            placeholder="Ask Anything"
            value={messageValue}
            onChange={(e) => {
              setMessageValue(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
            }}
            onKeyDown={(e) => {
              if (isBotBusy) return;
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
                e.target.style.height = "auto";
              }
            }}
            className={styles.input_field}
            rows={1}
          />
          <button
            type="button"
            className={styles.input_upload_btn}
            onClick={() => setShowUploadModal(true)}
            title="Upload documents"
            aria-label="Upload documents"
          >
            
            <GrUpload size={20} />
          </button>
          <button
            type="button"
            className={styles.input_send_btn}
            disabled={submitDisabled}
            onClick={(e) => handleSubmit(e)}
            title="Send"
            aria-label="Send"
          >
            <SendIcon />
          </button>
        </div>
      </div>

      {(ccmQuickActions || genCareplanAction) && (
        <div className={styles.genCareplanRow}>
          {ccmQuickActions}
          {genCareplanAction}
        </div>
      )}

      {/* Quick-prompt chips commented out per product request.
      {tab !== "CCM" && (
        <div className={styles.quick_prompts}>
          {QUICK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className={styles.quick_prompt_btn}
              onClick={() => handleQuickPrompt(prompt)}
              disabled={isBotBusy || isGeneratingNotes}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}
      */}
    </div>
  );
};

const Chatbot = ({ chatProps }) => {
  const {
    chatBotType,
    isChatStarted,
    setIsChatStarted,
    isCcmChatStarted,
    setIsCcmChatStarted,
    messages,
    message,
    setMessage,
    ccmMessages,
    ccmMessage,
    setCcmMessage,
    chatContainerRef,
    setDrNoteReceived,
    setDrNote,
    handleSubmit,
    handleCcmQuickAction,
    ccmQuickAction,
    normalizeCcmChatbot,
    resetCcmEndpointMode,
    setSessionId,
    setIsChatStartedWithReport,
    setCcmSessionId,
    setIsCcmChatStartedWithReport,
    showUploadModal,
    setShowUploadModal,
    sessionId,
    ccmSessionId,
    setFileRef,
    onNotesGenerated,
    clearMessagesForActiveTab
  } = chatProps;

  const patient_id = sessionStorage.getItem("_pt_id");

  const queryClient = useQueryClient();
  const { isCurrentMonthCareplanExists } = useToggle();
  const [hasFetched, setHasFetched] = useState(false);
  const [isCarePlanModalOpen, setIsCarePlanModalOpen] = useState(false);
  const [rpmReportsUploading, setRpmReportsUploading] = useState(false);
  const [uploadedRpmReports, setUploadedRpmReports] = useState([]);
  const [UploadingRpmReport, setUploadingRpmReport] = useState(false);
  const [resetCareplanModalOpen, setResetCareplanModalOpen] = useState(false);

  const careplanCreated = JSON.parse(sessionStorage.getItem("plan_created"));

  const [showScrollButton, setShowScrollButton] = useState(false);

  const genBtnRef = useRef(null); // To grab the button location

  const [isGenDropdownOpen, setIsGenDropdownOpen] = useState(false);

  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    isAbove: false
  }); // To store screen coordinates for dynamic positioning of dropdown

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [phoneAlertOpen, setPhoneAlertOpen] = useState(false);

  const [phoneAlertMessage, setPhoneAlertMessage] = useState(
    'This patient does not have a primary phone number on file. Please generate care plan using the "Generate Using Chat Only" option.'
  );

  // Toolbar Generate Notes (top-right circular button) state
  const toolbarGenBtnRef = useRef(null);
  const [isToolbarGenOpen, setIsToolbarGenOpen] = useState(false);
  const [toolbarDropdownPos, setToolbarDropdownPos] = useState({ top: 0, left: 0, isAbove: false });
  const [isConfirmGenerateNotesOpen, setIsConfirmGenerateNotesOpen] = useState(false);
  const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);

  // Restart chat state
  const [isRestartConfirmOpen, setIsRestartConfirmOpen] = useState(false);

  const { data: phoneCheckData } = useQuery(
    ["patient-phone-check", patient_id],
    () => checkPatientPhone(patient_id),
    { enabled: !!patient_id }
  );

  const { data: patientDetails } = useQuery(
    ["patient-general-details", patient_id],
    () => getPatientGeneralDetails(patient_id),
    { enabled: !!patient_id }
  );

  const [carePlanExist, setCarePlanExist] = useState(
    JSON.parse(sessionStorage.getItem("plan_created"))
  );
  const [previousMonthSelected, setPreviousMonthSelected] = useState(
    JSON.parse(sessionStorage.getItem("isCurrentMonth"))
  );
  // CCM-specific doctor note/report upload state removed (replaced by Context Selection)

  // Document upload modal state

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [rpmUploadedDocuments, setRpmUploadedDocuments] = useState([]);
  const [ccmUploadedDocuments, setCcmUploadedDocuments] = useState([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [hasClearedRpmStartPayload, setHasClearedRpmStartPayload] = useState(false);

  // Document upload mutation
  const patientDocumentsMutation = useMutation(
    formData => uploadPatientDocuments(formData),
    {
      onError: error => {
        toast.error("Failed to upload documents. Please try again.");
        console.error("Upload error:", error);
      }
    }
  );

  // ensureSessionIdForUpload: the AI upload endpoint requires a session_id,
  // but at welcome-screen time none exists yet. Generate one locally and
  // push it up via setSessionId/setCcmSessionId so the Start Chat useEffect
  // in patientDetail.js reuses it instead of generating its own.
  const ensureSessionIdForUpload = () => {
    const isCCM = chatBotType === "CCM";
    const existing = isCCM ? ccmSessionId : sessionId;
    if (existing) return existing;
    const generated = Math.floor(100000000 + Math.random() * 900000000);
    if (isCCM) {
      setCcmSessionId(generated);
      sessionStorage.setItem("ccm_session_id", generated);
    } else {
      setSessionId(generated);
    }
    return generated;
  };

  const handleDocumentsUpload = async () => {
    if (uploadedFiles.length <= 0) {
      toast.error("Please select at least one document to upload.");
      return;
    }

    // Validate file extensions
    const allowedExtensions = ["pdf"];
    const invalidFiles = uploadedFiles.filter((_doc) => {
      const fileName = _doc.file.name.toLowerCase();
      const extension = fileName.split(".").pop();
      return !allowedExtensions.includes(extension);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type detected. Allowed types: .pdf`);
      return;
    }

    setIsUploadingDocuments(true);
    try {
      setFileRef(null);

      // Backend upload payload (existing flow).
      const backendFormData = new FormData();
      uploadedFiles.forEach((_doc, i) => {
        backendFormData.append("documents", _doc.file);
        backendFormData.append(`document_type_${i}`, _doc.type);
      });

      // AI upload payload (new flow). Same File objects, different field name
      // ("files") plus the session_id the AI endpoint requires.
      const activeSessionId = ensureSessionIdForUpload();
      const aiFormData = new FormData();
      uploadedFiles.forEach((_doc) => {
        aiFormData.append("files", _doc.file);
      });
      aiFormData.append("session_id", String(activeSessionId));

      // Fire both uploads in parallel. Backend failure aborts the whole flow
      // (modal stays open, toast shown via mutation.onError); AI failure is
      // soft — the document is still saved, chat just starts without file_ref.
      const [backendResult, aiResult] = await Promise.allSettled([
        patientDocumentsMutation.mutateAsync(backendFormData),
        axios.post(`${BASE_URL_ML}/chatbot/chat/upload-file`, aiFormData, {
          headers: { "Content-Type": "multipart/form-data", accept: "application/json" }
        })
      ]);

      if (backendResult.status === "rejected") {
        return;
      }

      toast.success("Documents uploaded successfully!");
      queryClient.invalidateQueries(["patient-documents"]);

      if (chatBotType === "RPM") {
        const uploadedDocMeta = uploadedFiles.map((_doc, index) => ({
          id: `${Date.now()}-${index}-${_doc.file.name}`,
          name: _doc.file.name
        }));
        setRpmUploadedDocuments((prev) => {
          const existingNames = new Set(prev.map((doc) => doc.name));
          const nextBatch = uploadedDocMeta.filter((doc) => !existingNames.has(doc.name));
          return [...prev, ...nextBatch];
        });
      } else if (chatBotType === "CCM") {
        const uploadedDocMeta = uploadedFiles.map((_doc, index) => ({
          id: `${Date.now()}-${index}-${_doc.file.name}`,
          name: _doc.file.name
        }));
        setCcmUploadedDocuments((prev) => {
          const existingNames = new Set(prev.map((doc) => doc.name));
          const nextBatch = uploadedDocMeta.filter((doc) => !existingNames.has(doc.name));
          return [...prev, ...nextBatch];
        });
      }

      if (aiResult.status === "fulfilled") {
        const newFileRef = aiResult.value?.data?.file_ref;
        if (newFileRef) {
          setFileRef(newFileRef);
        } else {
          console.warn("AI upload succeeded but no file_ref in response");
        }
      } else {
        console.error("AI upload failed:", aiResult.reason);
        toast.error("Document saved, but AI processing failed.");
      }

      setShowUploadModal(false);
      setUploadedFiles([]);
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  // CarePlanModal is removed due to new careplan formate
  // const openCarePlanModal = () => {
  //   setIsCarePlanModalOpen(true);
  //   document.body.style.overflow = "hidden";
  // };

  // CarePlanModal is removed due to new careplan formate
  // const closeCarePlanModal = () => {
  //   setIsCarePlanModalOpen(false);
  //   document.body.style.overflow = "auto";
  // };


  const handleGenerateCareplan = (includeRecording = true) => {
    window.dispatchEvent(
      new CustomEvent("create_careplan_with_chatbot", {
        detail: { includeRecording }
      })
    );
    // Removed normalizeCcmChatbot() from here. It will now be triggered by an event on success.
  };

  const handleConfirmGeneration = () => {
    // This calls the original function to start generating
    handleGenerateCareplan(true);
    // Then closes the popup
    setIsConfirmOpen(false);
  };

  // ------- Toolbar Generate Notes flow (delivers text to monitoring textarea) -------
  // Distinct from handleGenerateCareplan above: this hits /chatbot/generate-notes
  // and pushes the resulting text to the monitoring panel via onNotesGenerated.
  const handleGenerateChatbotNotes = async (includeRecording) => {
    const isCCM = chatBotType === "CCM";
    const type = isCCM ? "CCM" : "RPM";
    const activeSessionId = isCCM ? ccmSessionId : sessionId;

    if (!activeSessionId) {
      toast.error("No active chat session. Start a chat first.");
      return;
    }

    setIsGeneratingNotes(true);
    try {
      const url = `${BASE_URL_ML}/chatbot/generate-notes?patient_type=${type}&session_id=${activeSessionId}&patient_id=${patient_id}&include_recording=${includeRecording}`;
      const response = await axios.get(url);

      const recordingStatus = response.data?.recording_status?.status;
      const processingSource = response.data?.recording_status?.processing_source;

      if (recordingStatus === "no_recording" && processingSource !== "chat_only") {
        setPhoneAlertMessage(
          "The most recent RingCentral call with the patient was not recorded. Use the chat conversation to generate Notes."
        );
        setPhoneAlertOpen(true);
        return;
      }
      if (recordingStatus === "recording_processing") {
        setPhoneAlertMessage(
          "Your RingCentral call recording is still being processed. Please wait 1 to 2 minutes before attempting to regenerate."
        );
        setPhoneAlertOpen(true);
        return;
      }
      if (recordingStatus === "call_active") {
        setPhoneAlertMessage(
          "The RingCentral call with the patient is still in progress. Please end the call before generating Notes."
        );
        setPhoneAlertOpen(true);
        return;
      }
      if (recordingStatus === "voicemail") {
        setPhoneAlertMessage(
          "The most recent RingCentral call was a voicemail. Please generate notes using the chat conversation only."
        );
        setPhoneAlertOpen(true);
        return;
      }

      const generated = response.data?.notes;
      const notesText =
        typeof generated === "string"
          ? generated
          : generated?.notes || generated?.note || generated?.text || "";

      if (notesText && typeof onNotesGenerated === "function") {
        onNotesGenerated(notesText);
        toast.success("Notes sent to monitoring panel");
      } else if (!notesText) {
        toast.error("No notes were returned");
      }
    } catch (error) {
      toast.error("Could not generate notes");
    } finally {
      setIsGeneratingNotes(false);
    }
  };

  const handleConfirmGenerateNotes = () => {
    handleGenerateChatbotNotes(true);
    setIsConfirmGenerateNotesOpen(false);
  };

  // ------- Restart chat -------
  // Clear messages, reset session, and return to the intro screen (which now
  // has a Start Chat button; the document-selection step was removed).
  const handleRestartChat = () => {
    if (typeof clearMessagesForActiveTab === "function") {
      clearMessagesForActiveTab();
    }
    if (chatBotType === "CCM") {
      setIsCcmChatStarted(false);
      setCcmUploadedDocuments([]);
      setUploadedFiles([]);
    } else {
      setIsChatStarted(false);
      setRpmUploadedDocuments([]);
      setUploadedFiles([]);
    }
    setIsRestartConfirmOpen(false);
    toast.success("Chat restarted");
  };

  const removeRpmUploadedDocument = (docId) => {
    setRpmUploadedDocuments((prev) => prev.filter((doc) => doc.id !== docId));
    setFileRef(null);
  };

  const handleStartRpmChatWithoutFiles = () => {
    setMessage("");
    setFileRef(null);
    setUploadedFiles([]);
    setRpmUploadedDocuments([]);
    setHasClearedRpmStartPayload(false);
    setIsChatStarted(true);
  };

  const handleStartRpmChatWithFiles = () => {
    setMessage("");
    setHasClearedRpmStartPayload(false);
    setRpmUploadedDocuments([]);
    setIsChatStarted(true);
  };

  const removeCcmUploadedDocument = (docId) => {
    setCcmUploadedDocuments((prev) => prev.filter((doc) => doc.id !== docId));
    setFileRef(null);
  };

  const handleStartCcmChatWithoutFiles = () => {
    setCcmMessage("");
    setFileRef(null);
    setUploadedFiles([]);
    setCcmUploadedDocuments([]);
    setIsCcmChatStarted(true);
  };

  const handleStartCcmChatWithFiles = () => {
    setCcmMessage("");
    setCcmUploadedDocuments([]);
    setIsCcmChatStarted(true);
  };

  useEffect(() => {
    if (chatBotType !== "RPM") return;

    if (!isChatStarted) {
      if (hasClearedRpmStartPayload) {
        setHasClearedRpmStartPayload(false);
      }
      return;
    }

    // After the first start-chat message appears, clear the welcome payload
    // state so text field and uploaded chips are clean for ongoing chat.
    if (!hasClearedRpmStartPayload && messages.length > 0) {
      setMessage("");
      setUploadedFiles([]);
      setRpmUploadedDocuments([]);
      setHasClearedRpmStartPayload(true);
    }
  }, [chatBotType, isChatStarted, messages.length, hasClearedRpmStartPayload, setMessage]);

  // ------- Quick prompt click -------
  // Quick-prompt chips are commented out per product request; handler kept
  // (disabled) so it can be restored alongside the chips.
  // const handleQuickPrompt = (promptText) => {
  //   const isCCM = chatBotType === "CCM";
  //   const list = isCCM ? ccmMessages : messages;
  //   if (list.length > 0 && list[list.length - 1].loading) return;
  //   if (isCCM) {
  //     setCcmMessage(promptText);
  //   } else {
  //     setMessage(promptText);
  //   }
  //   // Defer to next tick so the message state propagates before handleSubmit reads it
  //   setTimeout(() => {
  //     handleSubmit({ preventDefault: () => {} });
  //   }, 0);
  // };

  // Toolbar Generate Notes dropdown positioning
  const updateToolbarDropdownPos = () => {
    if (toolbarGenBtnRef.current) {
      const rect = toolbarGenBtnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const showAbove = spaceBelow < 180;
      setToolbarDropdownPos({
        top: showAbove ? rect.top : rect.bottom,
        left: rect.left + rect.width / 2,
        isAbove: showAbove
      });
    }
  };

  useEffect(() => {
    if (isToolbarGenOpen) {
      updateToolbarDropdownPos();
      window.addEventListener("scroll", updateToolbarDropdownPos, true);
      window.addEventListener("resize", updateToolbarDropdownPos);
    }
    return () => {
      window.removeEventListener("scroll", updateToolbarDropdownPos, true);
      window.removeEventListener("resize", updateToolbarDropdownPos);
    };
  }, [isToolbarGenOpen]);

  useEffect(() => {
    const clearUploadedDocuments = () => {
      setRpmUploadedDocuments([]);
      setCcmUploadedDocuments([]);
    };

    window.addEventListener("clear_chat_uploaded_documents", clearUploadedDocuments);
    return () => {
      window.removeEventListener("clear_chat_uploaded_documents", clearUploadedDocuments);
    };
  }, []);

  // Listen for successful careplan generation to clear chat
  useEffect(() => {
    const handleSuccess = () => {
      normalizeCcmChatbot();
    };

    window.addEventListener("careplan_generation_success", handleSuccess);

    return () => {
      window.removeEventListener("careplan_generation_success", handleSuccess);
    };
  }, [normalizeCcmChatbot]);

  // Listen for a successful Restart Care Plan (distinct from Restart Chat)
  // to reset the CCM endpoint mode back to /chatbot/ccm/chat.
  useEffect(() => {
    const handleCareplanReset = () => {
      if (typeof resetCcmEndpointMode === "function") {
        resetCcmEndpointMode();
      }
    };
    window.addEventListener("careplan_reset_success", handleCareplanReset);
    return () => {
      window.removeEventListener("careplan_reset_success", handleCareplanReset);
    };
  }, [resetCcmEndpointMode]);

  // Get the previous month
  const today = new Date();
  const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const previousMonth = {
    month: prevMonthDate.getMonth() + 1,
    year: prevMonthDate.getFullYear()
  };

  useEffect(() => {
    const handleCustomUpdate = () => {
      const value = JSON.parse(sessionStorage.getItem("plan_created"));
      setCarePlanExist(value);
    };

    window.addEventListener("plan_creation_updated", handleCustomUpdate);

    return () => {
      window.removeEventListener("plan_creation_updated", handleCustomUpdate);
    };
  }, []);

  useEffect(() => {
    const handleMonthChange = () => {
      const value = JSON.parse(sessionStorage.getItem("isCurrentMonth"));
      setPreviousMonthSelected(value);
    };

    window.addEventListener("month_change_update", handleMonthChange);

    return () => {
      window.removeEventListener("month_change_update", handleMonthChange);
    };
  }, []);

  // fetch current month careplan
  const {
    data: currentCarePlan,
    isFetching: current_carePlan_loading,
    isError: currentCarePlanError,
    refetch: refetchCurrentCarePlan
  } = useQuery({
    queryKey: ["currentCarePlan", patient_id],
    queryFn: () => getCarePlan(previousMonth.month + 1, previousMonth.year)
  });

  // fetch previous month careplan
  const {
    data: PreviousCarePlan,
    isFetching: carePlan_loading,
    isError: carePlanError,
    refetch: refetchPreviousCarePlan
  } = useQuery({
    queryKey: ["previousCarePlan", patient_id],
    queryFn: () => getCarePlan(previousMonth.month, previousMonth.year),
    enabled: false
  });

  const handleGenerateButtonClick = async () => {
    const { data: previousData } = await refetchPreviousCarePlan();
    setHasFetched(true);

    if (previousData && Object.keys(previousData).length > 0) {
      setIsCcmChatStarted(true);
    }
  };

  const handleDeleteCareplan = () => {
    window.dispatchEvent(new Event("delete_careplan_trigger"));
    // Clear previously uploaded documents so the CCM welcome screen returns to
    // its initial "Generate Care Plan" + "Upload Document" buttons. Without
    // this, the upload-preview box would render because ccmUploadedDocuments
    // is still populated after the careplan is deleted.
    setCcmUploadedDocuments([]);
    setUploadedFiles([]);
    if (typeof setFileRef === "function") {
      setFileRef(null);
    }
  };

  // CCM-specific doctor note upload handlers removed

  const fetchRpmSessionFromReport = async () => {
    const url = `${BASE_URL_ML}/chatbot/upload-reports`;

    const reportData = new FormData();

    uploadedRpmReports.forEach(file => {
      reportData.append("files", file);
    });

    try {
      setUploadingRpmReport(true);
      const response = await axios.post(url, reportData, {
        headers: {
          "Content-Type": "multipart/form-data",
          accept: "application/json"
        }
      });
      return response.data;
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    } finally {
      setUploadingRpmReport(false);
    }
  };

  // CCM-specific report upload flow removed

  const handleSubmitRpmReports = async () => {
    if (uploadedRpmReports.length <= 0) {
      toast.error("Please upload atleast one report.");
    } else {
      const reportResponse = await fetchRpmSessionFromReport();
      toast.success(reportResponse?.message);
      setSessionId(reportResponse?.session_id);
      setIsChatStartedWithReport(true);
      setIsChatStarted(true);
    }
  };

  // CCM-specific report submit handler removed

  // CCM-specific doctor note submit/skip removed

  // scrolling related functions
  useEffect(() => {
    requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight
        });
      }
    });
  }, [chatBotType]);

  const checkScrollPosition = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        chatContainerRef.current;
      setShowScrollButton(scrollTop + clientHeight < scrollHeight - 20);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    const checkScrollPosition = () => {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          chatContainerRef.current;
        setShowScrollButton(scrollTop + clientHeight < scrollHeight - 20);
      }
    };

    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener("scroll", checkScrollPosition);
      return () => container.removeEventListener("scroll", checkScrollPosition);
    }
  }, []);

  // Auto-scroll to bottom when streaming
  useEffect(() => {
    const currentMessages = chatBotType === "RPM" ? messages : ccmMessages;
    const hasStreamingMessage = currentMessages.some(msg => msg.streaming || msg.loading);

    if (hasStreamingMessage && chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth"
      });
    }
  }, [messages, ccmMessages, chatBotType]);

  // functions related to RPM report events
  const handleRpmReportsChange = e => {
    const files = Array.from(e.target.files);
    setUploadedRpmReports(prev => [...prev, ...files]);
  };

  const handleRpmReportsDrop = e => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setUploadedRpmReports(prev => [...prev, ...files]);
  };

  const handleRpmReportsClick = () => {
    document.getElementById("rpm_report_file_input").click();
  };

  // Helper to calculate position based on screen space
  const updateDropdownPosition = () => {
    if (genBtnRef.current) {
      const rect = genBtnRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // If we have less than 150px of space below, show it ABOVE
      const spaceBelow = windowHeight - rect.bottom;
      const showAbove = spaceBelow < 150;

      setDropdownPos({
        top: showAbove ? rect.top : rect.bottom, // If above, anchor to top of button. If below, anchor to bottom.
        left: rect.left + rect.width / 2, // Center horizontally
        isAbove: showAbove
      });
    }
  };

  // Listen for scroll/resize events ONLY when the dropdown is open
  useEffect(() => {
    if (isGenDropdownOpen) {
      // Run once immediately
      updateDropdownPosition();

      // Add listeners to update position if user scrolls the page
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
    }

    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isGenDropdownOpen]);

  // CCM quick actions push a bot turn of their own, so they must be blocked
  // whenever the CCM bot is already answering (chat turn or another quick action).
  const lastCcmMessage = ccmMessages[ccmMessages.length - 1];
  const isCcmBotBusy =
    !!ccmQuickAction || !!(lastCcmMessage?.loading || lastCcmMessage?.streaming);
  const ccmReferenceResult = useMemo(() => {
    for (let i = ccmMessages.length - 1; i >= 0; i -= 1) {
      const msg = ccmMessages[i];
      if (msg.type !== "bot" || !msg.quickAction || !msg.text) continue;
      const meta = CCM_REFERENCE_LABELS[msg.quickAction];
      if (!meta) continue;
      const conditionCount = (msg.text.match(/^## /gm) || []).length;
      return {
        action: msg.quickAction,
        label: meta.label,
        text: msg.text,
        summary:
          conditionCount > 0
            ? `${conditionCount} condition${conditionCount === 1 ? "" : "s"}`
            : ""
      };
    }
    return null;
  }, [ccmMessages]);

  const renderCcmQuickAction = (action, label) => (
    <button
      type="button"
      className={styles.ccmQuickActionBtn}
      onClick={() => handleCcmQuickAction?.(action)}
      disabled={isCcmBotBusy}
      title={label}
      aria-label={label}
    >
      <span>{label}</span>
    </button>
  );

  return (
    <>
      {chatBotType === "RPM" ? (
        <>
          {!isChatStarted && (
            <ChatbotWelcomePanel
              title="Start chatting with TOCA AI"
              subtitle="AI-powered guidance for Remote Patient Monitoring calls"
              steps={RPM_STEPS}
              onStartWithoutFiles={handleStartRpmChatWithoutFiles}
              onStartWithFiles={handleStartRpmChatWithFiles}
              isUploadingDocuments={isUploadingDocuments}
              uploadedDocuments={rpmUploadedDocuments}
              onRemoveUploadedDocument={removeRpmUploadedDocument}
              setShowUploadModal={setShowUploadModal}
              styles={styles}
            />
          )}

          {isChatStarted && (
            <ChatbotRedesignedPanel
              tab="RPM"
              messages={messages}
              messageValue={message}
              setMessageValue={setMessage}
              chatContainerRef={chatContainerRef}
              checkScrollPosition={checkScrollPosition}
              scrollToBottom={scrollToBottom}
              showScrollButton={showScrollButton}
              handleSubmit={handleSubmit}
              isGeneratingNotes={isGeneratingNotes}
              isToolbarGenOpen={isToolbarGenOpen}
              setIsToolbarGenOpen={setIsToolbarGenOpen}
              toolbarGenBtnRef={toolbarGenBtnRef}
              toolbarDropdownPos={toolbarDropdownPos}
              updateToolbarDropdownPos={updateToolbarDropdownPos}
              patientDetails={patientDetails}
              phoneCheckData={phoneCheckData}
              setPhoneAlertMessage={setPhoneAlertMessage}
              setPhoneAlertOpen={setPhoneAlertOpen}
              setIsConfirmGenerateNotesOpen={setIsConfirmGenerateNotesOpen}
              handleGenerateChatbotNotes={handleGenerateChatbotNotes}
              setIsRestartConfirmOpen={setIsRestartConfirmOpen}
              isUploadingDocuments={isUploadingDocuments}
              uploadedDocuments={rpmUploadedDocuments}
              onRemoveUploadedDocument={removeRpmUploadedDocument}
              setShowUploadModal={setShowUploadModal}
              styles={styles}
            />
          )}
        </>
      ) : (
        <>
          {!isCcmChatStarted && (
            <div className={styles.questions_box}>
              <div className={styles.welcome_container}>
                <img
                  src="/assets/images/chat.png"
                  alt="toca co_pilot"
                  className={styles.chatbot_image_small}
                />
                <h3>CCM CoPilot</h3>
                <p className={styles.welcome_subtitle}>
                  AI-powered Chronic Care Management plan builder
                </p>

                {!careplanCreated ? (
                  <div className={styles.instructions_container}>
                    <div className={styles.instruction_step}>
                      <span className={styles.step_badge}>1</span>
                      <div className={styles.step_content}>
                        <div className={styles.step_title}>Upload Provider Documentation</div>
                        <div className={styles.step_desc}>
                          Upload the doctor's note; CoPilot extracts key clinical details
                        </div>
                      </div>
                    </div>
                    <div className={styles.instruction_step}>
                      <span className={styles.step_badge}>2</span>
                      <div className={styles.step_content}>
                        <div className={styles.step_title}>Review Patient Information</div>
                        <div className={styles.step_desc}>
                          Verify demographics and clinical info before the call
                        </div>
                      </div>
                    </div>
                    <div className={styles.instruction_step}>
                      <span className={styles.step_badge}>3</span>
                      <div className={styles.step_content}>
                        <div className={styles.step_title}>Guided Conversation</div>
                        <div className={styles.step_desc}>
                          CoPilot generates suggested questions and surfaces relevant info during the call
                        </div>
                      </div>
                    </div>
                    <div className={styles.instruction_step}>
                      <span className={styles.step_badge}>4</span>
                      <div className={styles.step_content}>
                        <div className={styles.step_title}>Post-Call Capture</div>
                        <div className={styles.step_desc}>
                          Upload call transcript or enter additional information
                        </div>
                      </div>
                    </div>
                    <div className={styles.instruction_step}>
                      <span className={styles.step_badge}>5</span>
                      <div className={styles.step_content}>
                        <div className={styles.step_title}>Generate Care Plan</div>
                        <div className={styles.step_desc}>
                          CoPilot produces a structured, reviewable care plan
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.update_info_box}>
                    <p className={styles.update_info_text}>
                      Your existing care plan is loaded. Enter new or updated information
                      — progress notes, symptoms, follow-up outcomes, or treatment changes
                      — then click <strong>Update Care Plan</strong>. Unchanged sections
                      are preserved automatically.
                    </p>
                  </div>
                )}

                {(careplanCreated || ccmUploadedDocuments.length === 0) && (
                  <div
                    className={`${styles.rpm_welcome_actions} ${styles.ccm_welcome_actions}`}
                    style={{
                      ...(previousMonthSelected
                        ? {}
                        : {
                          pointerEvents: "none",
                          opacity: 0.5,
                          cursor: "not-allowed",
                        }),
                    }}
                  >
                    <Button
                      id="copilot-ccm-chat-button-btn"
                      onClick={handleStartCcmChatWithoutFiles}
                      className={styles.start_button}
                    >
                      {careplanCreated ? "Update Care Plan" : "Generate Care Plan"}
                    </Button>

                    {careplanCreated ? (
                      <Button
                        variant="ghost"
                        className={styles.upload_document_btn}
                        onClick={() => setResetCareplanModalOpen(true)}
                      >
                        Restart Care Plan
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        className={styles.upload_document_btn}
                        onClick={() => setShowUploadModal(true)}
                        // loading={isUploadingDocuments}
                        disabled={isUploadingDocuments}
                      >
                        Upload Document
                      </Button>
                    )}
                  </div>
                )}

                {!careplanCreated && ccmUploadedDocuments.length > 0 && (
                  <div
                    className={`${styles.welcome_upload_box} ${styles.ccm_welcome_upload_box}`}
                    style={{
                      ...(previousMonthSelected
                        ? {}
                        : {
                          pointerEvents: "none",
                          opacity: 0.5,
                          cursor: "not-allowed",
                        }),
                    }}
                  >
                    <div className={styles.input_documents_row}>
                      {ccmUploadedDocuments.map((doc) => (
                        <div key={doc.id} className={styles.input_document_chip}>
                          <div className={styles.input_document_icon} aria-hidden="true">
                            <img src="/assets/images/pdficon.svg" alt="PDF" />
                          </div>
                          <div className={styles.input_document_text}>
                            <div className={styles.input_document_name}>{doc.name}</div>
                            <div className={styles.input_document_type}>PDF</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className={styles.welcome_upload_actions}>
                      <button
                        type="button"
                        className={styles.start_button}
                        disabled={isUploadingDocuments}
                        onClick={handleStartCcmChatWithFiles}
                        title="Start Chat"
                        aria-label="Start Chat"
                      >
                        Start Chat
                      </button>
                    </div>
                  </div>
                )}

                {careplanCreated && (
                  <ResetCareplan
                    isOpen={resetCareplanModalOpen}
                    setIsOpen={setResetCareplanModalOpen}
                    onReset={() => handleDeleteCareplan()}
                  />
                )}

                {!previousMonthSelected && (
                  <p style={{ marginTop: "15px", color: "#ff4b4b" }}>
                    {careplanCreated
                      ? "Select current month to Update or Restart Care Plan."
                      : "Please select current month to generate Care Plan."}
                  </p>
                )}
              </div>
            </div>
          )}

          {isCcmChatStarted && (
            <ChatbotRedesignedPanel
              tab="CCM"
              messages={ccmMessages}
              messageValue={ccmMessage}
              setMessageValue={setCcmMessage}
              chatContainerRef={chatContainerRef}
              checkScrollPosition={checkScrollPosition}
              scrollToBottom={scrollToBottom}
              showScrollButton={showScrollButton}
              handleSubmit={handleSubmit}
              isGeneratingNotes={isGeneratingNotes}
              isToolbarGenOpen={isToolbarGenOpen}
              setIsToolbarGenOpen={setIsToolbarGenOpen}
              toolbarGenBtnRef={toolbarGenBtnRef}
              toolbarDropdownPos={toolbarDropdownPos}
              updateToolbarDropdownPos={updateToolbarDropdownPos}
              patientDetails={patientDetails}
              phoneCheckData={phoneCheckData}
              setPhoneAlertMessage={setPhoneAlertMessage}
              setPhoneAlertOpen={setPhoneAlertOpen}
              setIsConfirmGenerateNotesOpen={setIsConfirmGenerateNotesOpen}
              handleGenerateChatbotNotes={handleGenerateChatbotNotes}
              setIsRestartConfirmOpen={setIsRestartConfirmOpen}
              isUploadingDocuments={isUploadingDocuments}
              uploadedDocuments={ccmUploadedDocuments}
              onRemoveUploadedDocument={removeCcmUploadedDocument}
              suppressPendingBubble={!!ccmQuickAction}
              referenceDrawer={
                <CcmReferenceDrawer
                  result={ccmReferenceResult}
                  pendingAction={ccmQuickAction}
                  pendingLabel={CCM_REFERENCE_LABELS[ccmQuickAction]?.label}
                />
              }
              ccmQuickActions={
                <div className={styles.ccmQuickActions}>
                  {renderCcmQuickAction(
                    "condition-guidelines",
                    "Condition Guidelines"
                  )}
                  {renderCcmQuickAction(
                    "warning-signs",
                    "Red-Flag Warning Signs"
                  )}
                </div>
              }
              genCareplanAction={
                <div
                  ref={genBtnRef}
                  className={styles.genCareplanWrapper}
                  onMouseEnter={() => {
                    updateDropdownPosition(); // Calculating position immediately on hover
                    setIsGenDropdownOpen(true);
                  }}
                  onMouseLeave={() => setIsGenDropdownOpen(false)}
                >
                  <Button
                    size="small"
                    variant="ghost"
                    type="button"
                    className={styles.genButton}
                  >
                    <FaSheetPlastic className="icon-default-size icon-default-color" />
                    <h4>Gen Careplan</h4>
                  </Button>

                  {isGenDropdownOpen && (
                    <div
                      className={styles.hoverDropdownMenu}
                      style={{
                        top: `${dropdownPos.top}px`,
                        left: `${dropdownPos.left}px`,
                        transform: dropdownPos.isAbove
                          ? "translate(-50%, -100%)"
                          : "translate(-50%, 0)",
                        marginTop: dropdownPos.isAbove ? "-10px" : "10px"
                      }}
                    >
                      {/* Invisible Bridge */}
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          width: "100%",
                          height: "20px", // Enough to bridge the gap
                          background: "transparent",
                          // If menu is ABOVE, bridge needs to be at BOTTOM of menu to reach button
                          // If menu is BELOW, bridge needs to be at TOP of menu to reach button
                          bottom: dropdownPos.isAbove ? "-20px" : "auto",
                          top: dropdownPos.isAbove ? "auto" : "-20px",
                          zIndex: -1
                        }}
                      />

                      <div
                        className={styles.hoverDropdownItem}
                        onClick={() => {
                          if (!patientDetails?.user?.primary_phone) {
                            setPhoneAlertMessage(
                              'This patient does not have a primary phone number on file. Please generate care plan using the "Generate Using Chat Only" option.'
                            );
                            setPhoneAlertOpen(true);
                            setIsGenDropdownOpen(false);
                            return;
                          }

                          if (phoneCheckData?.exists) {
                            setPhoneAlertMessage(
                              'This patient\'s phone number is associated with another patient. Please generate care plan using the "Generate Using Chat Only" option.'
                            );
                            setPhoneAlertOpen(true);
                            setIsGenDropdownOpen(false);
                            return;
                          }

                          setIsConfirmOpen(true);
                          setIsGenDropdownOpen(false);
                        }}
                      >
                        <div className={styles.hoverDropdownItemTitle}>Include Call Transcription</div>
                        <div className={styles.hoverDropdownItemDesc}>
                          Uses the RingCentral call recording along with chat history
                        </div>
                      </div>
                      <div
                        className={styles.hoverDropdownItem}
                        onClick={() => {
                          handleGenerateCareplan(false);
                          setIsGenDropdownOpen(false);
                        }}
                      >
                        <div className={styles.hoverDropdownItemTitle}>Generate Using Chat Only</div>
                        <div className={styles.hoverDropdownItemDesc}>
                          Generates the care plan from chat conversation only — no call recording
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              }
              setShowUploadModal={setShowUploadModal}
              styles={styles}
            />
          )}
        </>
      )}

      {/* CarePlanModal is removed due to new careplan formate */}
      {/* {isCarePlanModalOpen && (
        <CarePlanModal
          isOpen={isCarePlanModalOpen}
          onClose={closeCarePlanModal}
          fromAi={true}
          carePlanData={[]}
          carePlanCreated={false}
        />
      )} */}

      {/* Documents upload Modal */}

      <Dialog open={showUploadModal} setOpen={() => setShowUploadModal(false)}>
        <DialogContent closeDisabled={isUploadingDocuments}>
          <DialogHeader>
            <DialogTitle>Upload Documents</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <section className={styles.upload_container}>
              <UploadFiles
                accept=".pdf,application/pdf"
                multiple={true}
                maxFiles={5}
                maxFileSize={10 * 1024 * 1024}
                showFileList={true}
                variant="chatbot"
                placeholder="Drop your files here"
                subText="Or click to browse"
                buttonText="Browse"
                disabled={isUploadingDocuments}
                filesHandler={files => setUploadedFiles(files)}
                onSuccess={patientDocumentsMutation.isSuccess}
                options={
                  chatBotType === "RPM"
                    ? [
                      { label: "Consent", value: "consent" },
                      { label: "Additional", value: "additional" },
                      { label: "Lab Reports", value: "lab" },
                      { label: "Imaging", value: "imaging" }
                    ]
                    : [
                      { label: "Careplan", value: "careplan" },
                      { label: "Additional", value: "additional" },
                      { label: "Doctor Note", value: "doctor" },
                      { label: "Lab Reports", value: "lab" },
                      { label: "Imaging", value: "imaging" }
                    ]
                }
              />
            </section>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowUploadModal(false)}
              disabled={isUploadingDocuments}
            >
              Cancel
            </Button>
            <Button
              disabled={uploadedFiles.length <= 0}
              loading={isUploadingDocuments}
              onClick={() => handleDocumentsUpload()}
            >
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Popup for RingCentral Call */}
      <AlertDialog
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        title="Confirm Call Completion"
        description="Please confirm that your patient call on RingCentral has ended."
      >
        <Button variant="ghost" onClick={() => setIsConfirmOpen(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirmGeneration}>
          Confirm
        </Button>
      </AlertDialog>
      <AlertDialog
        open={phoneAlertOpen}
        setOpen={setPhoneAlertOpen}
        title="Notification"
        description={phoneAlertMessage}
      >
        <Button variant="primary" onClick={() => setPhoneAlertOpen(false)}>
          OK
        </Button>
      </AlertDialog>

      {/* Toolbar Generate Notes confirm dialog (call-transcription path) */}
      <AlertDialog
        open={isConfirmGenerateNotesOpen}
        setOpen={setIsConfirmGenerateNotesOpen}
        title="Confirm Call Completion"
        description="Please confirm that your patient call on RingCentral has ended."
      >
        <Button variant="ghost" onClick={() => setIsConfirmGenerateNotesOpen(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirmGenerateNotes}>
          Confirm
        </Button>
      </AlertDialog>

      {/* Restart chat confirm dialog */}
      <AlertDialog
        open={isRestartConfirmOpen}
        setOpen={setIsRestartConfirmOpen}
        title="Restart chat?"
        description="This will clear the current conversation and return you to the start screen."
      >
        <Button variant="ghost" onClick={() => setIsRestartConfirmOpen(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleRestartChat}>
          Restart
        </Button>
      </AlertDialog>
    </>
  );
};

export default Chatbot;
